import {mergeProperties} from "@/shared/utils/misc";

export interface ComboboxConfig<T extends { id: string | number }> {
	items: T[];
	formatter: (item: T) => string;
	filterElement: JQuery;
	idElement: JQuery;
	caretElement: JQuery;
	classCaretDown?: string;
	classCaretUp?: string;
	classClear?: string;
	clearable?: boolean;
	/**
	 * Placeholder message displayed if no element is selected.
	 */
	emptyMessage?: string;
	/**
	 * Adds a dummy suggestion element with this message that clears the selection on click.
	 */
	clearEntryMessage?: string;
	/**
	 * Message displayed in the dropdown when a search query yields no results.
	 */
	notFoundMessage?: string;
}

export class Combobox<T extends { id: string | number }> {

	public static readonly EVENT_CHANGED = "changed";

	private readonly config: ComboboxConfig<T>;
	private source: Bloodhound<T>;
	private selectedItem: T = null;
	private caretWillClick = false;

	constructor(config: ComboboxConfig<T>) {
		this.config = mergeProperties({
			classCaretDown: "fa-caret-down",
			classCaretUp: "fa-caret-up",
			classClear: "fa-times",
			clearable: true,
			emptyMessage: ""
		}, config);
		this.init();
	}

	public updateItems(items: T[], selectedItem: T = null, preSelectFirstItem = false, addItemIfMissing = false) {
		this.config.items = items;
		this.config.filterElement.typeahead("destroy");
		this.initSource();
		this.initTypeahead();
		if (selectedItem !== null) {
			this.setSelectedItem(selectedItem, preSelectFirstItem, addItemIfMissing);
		} else if (this.selectedItem === null || !this.containsItem(this.selectedItem)) {
			if (preSelectFirstItem) {
				this.selectFirstItem();
			} else {
				this.clearSelection();
			}
		}
	}

	public getSelectedItem(): T {
		return this.selectedItem;
	}

	public setSelectedItem(item: T, preSelectFirstItem = false, addItemIfMissing = false, fireChangedEvent: boolean = true) {
		if (this.containsItem(item)) {
			this.select(item, fireChangedEvent);
		} else if (addItemIfMissing) {
			this.updateItems(this.config.items.concat([item]));
			this.select(item, fireChangedEvent);
		} else if (preSelectFirstItem) {
			this.selectFirstItem(fireChangedEvent);
		}
	}

	public setSelectedItemById(id: number | string, preSelectFirstItem = false, fireChangedEvent: boolean = true) {
		const item = this.findItemById(id);
		if (item) {
			this.setSelectedItem(item, preSelectFirstItem, false, fireChangedEvent);
		} else if (preSelectFirstItem) {
			this.selectFirstItem(fireChangedEvent);
		}
	}

	public selectFirstItem(fireChangedEvent: boolean = true) {
		if (this.config.items.length > 0) {
			this.select(this.config.items[0], fireChangedEvent);
		} else {
			this.clearSelection();
		}
	}

	public setDisabled(disabled: boolean) {
		this.config.filterElement.prop("disabled", disabled);
		this.config.caretElement.prop("disabled", disabled).toggleClass("disabled", disabled);
	}

	public setVisible(visible: boolean) {
		if (visible) {
			this.config.filterElement.show();
			this.config.caretElement.show();
		} else {
			this.config.filterElement.hide();
			this.config.caretElement.hide();
		}
	}

	public clearSelection(forceClearSelection = false) {
		if (this.selectedItem !== null || forceClearSelection) {
			this.selectedItem = null;
			this.showSelectedItem();
			this.onChanged();
		} else {
			// Always update the filter element to show the empty message
			this.showSelectedItem();
		}
		this.updateCaret(false);
	}

	public setEmptyMessage(emptyMessage: string) {
		this.config.emptyMessage = emptyMessage;
	}

	public getItems() {
		return this.config.items;
	}

	private init() {
		this.initSource();
		this.initTypeahead();
		this.initCaret();
		this.showSelectedItem();
	}

	private initSource() {
		const whiteSpaceSpecialTokenizer = (str: string) => {
			str = str ? "" + str : "";
			// Whitespace plus additional ranges for ascii special characters
			return str.split(/[\s!-/:-@[-`{-~]+/);
		};

		/* DEVNOTE removed identify:() => number override with TIMR-6857, so bloodhound use its default implementation
		 * This was necessary to allow string IDs and I could not see any difference without it
		 */
		this.source = new Bloodhound<T>({
			local: this.config.items,
			queryTokenizer: (query) => whiteSpaceSpecialTokenizer(query),
			datumTokenizer: (item) => whiteSpaceSpecialTokenizer(this.config.formatter(item))
		});
	}

	private initTypeahead() {
		this.config.filterElement.keydown((e) => {
			if (e.keyCode === 9) { // Keycode Tab
				if (this.config.filterElement.val() === "") {
					// Prevent autocomplete on empty filter element otherwise it will just autocomplete the first element
					// This allows to tab through the input elements without selection anything
					e.stopImmediatePropagation();
				}
			}
		});
		this.config.filterElement.typeahead<T>({
			minLength: 0
		}, {
			name: "items",
			async: false, // required for the "notFound" template to render; without it, typeahead waits indefinitely for async results (all Comboboxes use local-only Bloodhound sources)
			display: (item) => {
				return this.config.formatter(item);
			},
			limit: 1000,
			source: ((query, syncResults, asyncResults) => {
				if (query === "") {
					syncResults(this.config.items);
				} else {
					this.source.search(query, syncResults, asyncResults);
				}
			}),
			templates: {
				header: (query, suggestions) => {
					if (this.config.clearEntryMessage) {
						return `<div class="tt-suggestion tt-selectable dummy-suggestion">${this.config.clearEntryMessage}</div>`;
					} else {
						return "";
					}
				},
				notFound: () => {
					if (this.config.notFoundMessage) {
						return `<div class="tt-not-found-message text-muted">${this.config.notFoundMessage}</div>`;
					} else {
						return "";
					}
				}
			}
		}).off("typeahead:beforeopen typeahead:open typeahead:select typeahead:beforeclose typeahead:close typeahead:render typeahead:autocompleted")
			.on("typeahead:beforeopen", () => {
				this.config.filterElement.closest(".twitter-typeahead").find(".tt-hint").removeClass("text-white");
				this.clearFilter();
			}).on("typeahead:open", () => {
			this.updateCaret(true);
			this.clearFilter();
		}).on("typeahead:select", (e, item: T) => {
			this.select(item, true);
			this.config.filterElement.blur();
		}).on("typeahead:beforeclose", () => {
			return !this.caretWillClick;
		}).on("typeahead:close", () => {
			this.config.filterElement.closest(".twitter-typeahead").find(".tt-hint").addClass("text-white");
			this.updateCaret(false);
			this.showSelectedItem();
		}).on("typeahead:render", (e, firstSuggestion) => {
			if (this.config.clearEntryMessage) {
				const headerElement = this.config.filterElement.closest(".twitter-typeahead")
					.find("div.tt-menu > div.tt-dataset.tt-dataset-items > div.dummy-suggestion");

				headerElement.click(() => {
					this.clearSelection();

					// Blur activeElement so cursor is not positioned at the end of the placeholder after clear
					if (document.activeElement instanceof HTMLElement) {
						document.activeElement.blur();
					}
				});
			}
		}).on("typeahead:autocompleted", (e, item: T) => {
			// We only autocomplete on whole elements so we can immediately select them on autocomplete
			this.select(item, true);
			this.config.filterElement.blur();
		});
		this.config.filterElement.closest(".twitter-typeahead").find(".tt-hint").addClass("text-white");
	}

	private initCaret() {
		this.config.caretElement.mousedown(() => {
			this.caretWillClick = true;
		}).mouseup(() => {
			this.caretWillClick = false;
		}).mouseleave(() => {
			this.caretWillClick = false;
		}).click(() => {
			if (this.config.filterElement.typeahead("isOpen")) {
				this.config.filterElement.typeahead("close");
			} else if (this.config.clearable && this.config.caretElement.children("i, span").hasClass(this.config.classClear)) {
				this.clearSelection();
			} else {
				this.config.filterElement.focus();
			}
		});
	}

	private updateCaret(open?: boolean) {
		const caretIconElement = this.config.caretElement.children("i, span");
		if (open ?? this.config.filterElement.typeahead("isOpen")) {
			caretIconElement.toggleClass(this.config.classCaretDown, false);
			caretIconElement.toggleClass(this.config.classCaretUp, true);
			caretIconElement.toggleClass(this.config.classClear, false);
		} else {
			const clearable = this.config.clearable && this.selectedItem != null;
			caretIconElement.toggleClass(this.config.classCaretDown, !clearable);
			caretIconElement.toggleClass(this.config.classCaretUp, false);
			caretIconElement.toggleClass(this.config.classClear, clearable);
		}
	}

	private containsItem(item: T): boolean {
		return item != null && this.findItemById(item.id) !== null;
	}

	private findItemById(id: number | string): T {
		return this.config.items.find(item => item.id === id) || null;
	}

	private clearFilter() {
		this.config.filterElement.typeahead("val", "");
	}

	private select(item: T, fireChangedEvent: boolean) {
		if (this.selectedItem !== item) {
			this.selectedItem = item;
			this.showSelectedItem();
			this.updateCaret();
			if (fireChangedEvent) {
				this.onChanged();
			}
		}
	}

	private showSelectedItem() {
		this.config.idElement.val(this.selectedItem ? this.selectedItem.id : "");
		this.config.filterElement.typeahead("val", this.selectedItem ? this.config.formatter(this.selectedItem) : this.config.emptyMessage);
	}

	private onChanged() {
		$(this).triggerHandler(Combobox.EVENT_CHANGED, [this.selectedItem]);
	}
}
